import * as R from "ramda";

export const urlHasQuery = (url) => !R.isNil(url) && url.indexOf("?") > -1;

export const appendResult = (url, result) => {
  const resultQs = R.pipe(
    R.toPairs,
    R.map(([k, v]) => {
      const val = ["string", "number"].includes(typeof v) ? v : JSON.stringify(v); // if value is a string or number pass along, otherwise it's either an array or object which we can stringify
      return `${encodeURIComponent(k)}=${encodeURIComponent(val)}`
    }),
    R.join("&")
  )(result);
  const delimiter = urlHasQuery(url) ? "&" : "?";
  return `${url}${delimiter}${resultQs}`;
};

export function processURLForXSS(url) {
  let u;
  try {
    u = new URL(url);
  } catch {
    return null; // invalid url that new URL() can't handle
  }
  const containsDangerousProtocol = ["javascript:", "data:"].includes(u.protocol);
  const containsNonStandardURLChar = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/.test(url); // not sure how much this one actually matters as we're not storing search params, etc
  const isBad = containsDangerousProtocol || containsNonStandardURLChar;
  return isBad ? null : url;
}

/**
 * RedirectNotAllowedError
 * @description Thrown when a redirect URL fails validation against the integrator's
 * allowed_domain_redirect_patterns. Recognized by GlobalErrorBoundary to show a
 * user-facing "Invalid redirect domain" message.
 */
export function RedirectNotAllowedError(message = "Redirect URL not allowed by integrator settings") {
  this.name = "RedirectNotAllowedError";
  this.message = message;
  this.stack = new Error(message).stack;
}
RedirectNotAllowedError.prototype = Object.create(Error.prototype);
RedirectNotAllowedError.prototype.constructor = RedirectNotAllowedError;

/**
 * isRedirectURLAllowed
 * @param {string} redirectUrl
 * @param {object} integratorSettings
 * @param {Array<string>} [integratorSettings.allowed_domain_redirect_patterns]
 * @description Compares the 'origin' of the redirectUrl to patterns allowed for origin from
 * what is listed in integratorSettings.  The 'origin' comes from the URL object (which drops path and search)
 */
export const isRedirectURLAllowed = (redirectUrl, integratorSettings) => {
  const { allowed_domain_redirect_patterns } = integratorSettings || {};
  let isAllowed = !allowed_domain_redirect_patterns; // TODO: This should default to false so we enforce having allowed patterns, but for now if allowed patterns are not included in settings we will allow any redirect
  try {
    const allowedPatterns = allowed_domain_redirect_patterns || [];
    const redirect = new URL(decodeURIComponent(redirectUrl)); // we want to be able to drop path and query values so we can just compare the origin of the request
    allowedPatterns.forEach((pattern) => {
      // Drop any trailing '/', escape all regex metacharacters so literal chars
      // (notably '.') are matched literally, then re-enable '*' as the intended
      // wildcard. Without escaping, "https://*.tilia.io" would also match
      // look-alike domains like "https://evil-tilia.io" since '.' would match any char.
      const escaped = pattern
        .replace(/\/$/, "")
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // escape regex metachars (turns '*' into '\*')
        .replace(/\\\*/g, ".+"); // then re-enable the intended wildcard
      const regExStr = `^${escaped}$`; // overall pattern is expected to match beginning and end of origin
      // eslint-disable-next-line security/detect-non-literal-regexp
      const re = new RegExp(regExStr);
      if (redirect.origin.match(re)) isAllowed = true;
    });
  } catch (err) {
    console.error(err);
  }
  return isAllowed;
};

export function redirectWindowLocation(url) {
  const processedUrl = processURLForXSS(url);
  if (processedUrl) {
    window.location.href = processedUrl;
  } else {
    const msg = "Invalid url";
    alert(msg);
    throw new Error(msg);
  }
}

export const redirectToIntegrator = (ctx, result, integratorSettings) => {
  if (!ctx.returnUrl) {
    console.error("No return complete URL!");
    return null;
  }
  // append the result to the return url and redirect
  const returnUrl = appendResult(ctx.returnUrl, result);
  const redirectAllowed = isRedirectURLAllowed(returnUrl, integratorSettings);
  if (redirectAllowed) {
    redirectWindowLocation(returnUrl);
    return returnUrl;
  } else {
    const msg = "Invalid url";
    alert(msg);
    throw new Error(msg);
  }
};
